/**
 * Day details modal — a port of `viewDayDetails` / `openDayModal`.
 *
 * Layout, wording and colours follow the legacy modal: the two action buttons,
 * the teal drag hint, one section per working clinic, a four-column slot grid,
 * the night-hours divider, and the cancel-day button at the foot.
 *
 * Drag-to-reschedule is implemented with real HTML5 drag events, as the legacy
 * version did — the hint bar promises it, so it has to work. What is *not*
 * carried over is the legacy's automatic re-shuffling of every later booking on
 * drop (`fillGapAfterMove` / `shiftBookingsAndInsert`): it moved appointments
 * patients had already been told about, without asking. A drop here moves the
 * one booking that was dragged.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../../components/common';
import { hospitalClock, isDue } from '../../../domain/discovery';
import { useI18n } from '../../../i18n/I18nProvider';
import { ModeBadge } from '../../discovery/parts';
import { useScrollLock } from '../../../hooks/useLocalStorage';
import {
  BTN_CANCEL_DAY,
  BTN_EXTRA_HOURS,
  BTN_OFFER,
  DRAG_HINT,
  MODAL_SURFACE,
  SLOT_FREE,
  SLOT_FREE_EXTRA,
  ADMIN_GLOW,
  SLOT_ADMIN_DISCOUNT,
  clinicHex,
  slotBookedStyle,
} from './legacyTheme';
import { clinicSlotsForDate } from '../../../domain/slots';
import { formatTime12 } from '../../../domain/dates';
import { BOOKING_TYPES, calculateBookingPrice } from '../../../domain/pricing';
import { bestDayDiscount } from '../../../domain/offers';

/** Full Arabic date, as the legacy header showed it. */
const dayLabel = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString('ar-EG', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export function DayDetailsModal({
  open,
  dateKey,
  clinics = [],
  bookings = [],
  offers = [],
  extraHours = [],
  hasOfferForDay = false,
  onClose,
  onSelectBooking,
  onBookSlot,
  onConfigureExtraHours,
  onCreateOffer,
  onCancelDay,
  onReopenDay,
  isCancelled = false,
  onMoveBooking,
}) {
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  useScrollLock(open);

  const dayExtraHours = useMemo(
    () => (extraHours || []).filter((entry) => entry.date === dateKey),
    [extraHours, dateKey],
  );

  const dayDiscount = useMemo(() => bestDayDiscount(offers, dateKey), [offers, dateKey]);

  if (!open) return null;

  const hasExtraHours = dayExtraHours.length > 0;
  const extraHoursLabel = hasExtraHours ? 'تعديل ساعات إضافية' : 'إضافة ساعات إضافية';
  const offerLabel = hasOfferForDay ? 'تعديل العرض' : 'إضافة عرض';

  const workingClinics = clinics.filter(
    (clinic) => clinicSlotsForDate(clinic, dateKey).length > 0,
  );

  const handleDrop = (event, time, clinicId) => {
    event.preventDefault();
    setDropTarget(null);
    if (!dragging) return;
    if (dragging.time === time) return;
    onMoveBooking?.(dragging.bookingId, time, clinicId);
    setDragging(null);
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 p-2 backdrop-blur-sm sm:p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      {/* The surface and the slot colours are the dark palette this grid was
          designed in, so its text keeps that palette too (theme-fixed): on a
          light theme `text-white` is ink and the day and clinic names vanished. */}
      <div
        className="theme-fixed max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl"
        style={{
          background: MODAL_SURFACE.bg,
          border: `1px solid ${MODAL_SURFACE.border}`,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Header — close button on the left, title on the right (RTL) */}
        <div className="flex items-center justify-between px-4 pb-4 pt-5 sm:px-6 sm:pt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="إغلاق"
          >
            <Icon name="x" size={20} />
          </button>
          <h3 className="text-xl font-bold text-white sm:text-2xl">{dayLabel(dateKey)}</h3>
        </div>

        <div className="space-y-6 px-3 pb-5 sm:px-6 sm:pb-6">
          {isCancelled && (
            <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-300">
              <Icon name="x-circle" size={18} className="shrink-0" />
              هذا اليوم مغلق — لا يمكن للمرضى الحجز فيه.
            </div>
          )}

          {/* Two action buttons */}
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => {
                onClose?.();
                onConfigureExtraHours?.(dateKey);
              }}
              className="flex items-center justify-center gap-2 rounded-xl py-4 font-bold text-white transition-all hover:brightness-110"
              style={{ background: BTN_EXTRA_HOURS.bg, border: `1px solid ${BTN_EXTRA_HOURS.border}` }}
            >
              {extraHoursLabel}
              <Icon name="clock" size={20} className="me-1" />
            </button>

            <button
              type="button"
              onClick={() => {
                onClose?.();
                onCreateOffer?.(dateKey);
              }}
              className="flex items-center justify-center gap-2 rounded-xl py-4 font-bold text-white transition-all hover:brightness-110"
              style={{ background: BTN_OFFER.bg, boxShadow: BTN_OFFER.glow }}
            >
              {offerLabel}
              <Icon name="percent" size={20} className="me-1" />
            </button>
          </div>

          {/* Drag hint */}
          <div
            className="flex items-center justify-center gap-2 rounded-xl px-4 py-3"
            style={{ background: DRAG_HINT.bg, border: `1px solid ${DRAG_HINT.border}` }}
          >
            <span className="text-sm" style={{ color: DRAG_HINT.text }}>
              اسحب الحجز لتغيير موعده - الحجوزات الأخرى لن تتأثر
            </span>
            <Icon name="move" size={16} className="shrink-0" style={{ color: DRAG_HINT.text }} />
          </div>

          {/* One section per clinic */}
          {workingClinics.length === 0 ? (
            <p className="py-8 text-center text-slate-400">
              لا توجد مواعيد لهذه العيادات في هذا اليوم.
            </p>
          ) : (
            workingClinics.map((clinic) => (
              <ClinicSection
                key={clinic.id}
                clinic={clinic}
                dateKey={dateKey}
                bookings={bookings}
                dayDiscount={dayDiscount}
                extraHours={dayExtraHours}
                dragging={dragging}
                dropTarget={dropTarget}
                onDragStart={(bookingId, time) => setDragging({ bookingId, time })}
                onDragEnd={() => {
                  setDragging(null);
                  setDropTarget(null);
                }}
                onDragOverSlot={(time) => setDropTarget(time)}
                onDragLeaveSlot={() => setDropTarget(null)}
                onDrop={handleDrop}
                onSelectBooking={onSelectBooking}
                onBookSlot={onBookSlot}
                onEditExtraHours={() => {
                  onClose?.();
                  onConfigureExtraHours?.(dateKey);
                }}
              />
            ))
          )}

          {/* Close the day, or reopen one that is already closed.
              Closing persists now, so there has to be a way back out of it. */}
          <div className="border-t border-white/10 pt-2">
            {isCancelled ? (
              <button
                type="button"
                onClick={() => onReopenDay?.(dateKey)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 py-3 font-bold text-emerald-300 transition-all hover:bg-emerald-500/20"
              >
                <Icon name="rotate-ccw" size={20} />
                إعادة فتح اليوم
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onCancelDay?.(dateKey)}
                className="flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold transition-all hover:brightness-110"
                style={{
                  background: BTN_CANCEL_DAY.bg,
                  border: `1px solid ${BTN_CANCEL_DAY.border}`,
                  color: BTN_CANCEL_DAY.text,
                }}
              >
                <Icon name="x-circle" size={20} />
                إلغاء اليوم
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========================================================================== */

function ClinicSection({
  clinic,
  dateKey,
  bookings,
  dayDiscount,
  extraHours,
  dragging,
  dropTarget,
  onDragStart,
  onDragEnd,
  onDragOverSlot,
  onDragLeaveSlot,
  onDrop,
  onSelectBooking,
  onBookSlot,
  onEditExtraHours,
}) {
  const regularSlots = clinicSlotsForDate(clinic, dateKey);

  const extraSlots = useMemo(
    () => extraHours.flatMap((entry) => entry.slots || []),
    [extraHours],
  );

  const nightDiscount = extraHours[0]?.hasNightOffer
    ? extraHours[0]?.nightOfferDiscount || 0
    : 0;

  const findBooking = (time) =>
    bookings.find(
      (b) =>
        b.time === time &&
        (String(b.clinicId) === String(clinic.id) || b.clinicName === clinic.name) &&
        b.status !== 'cancelled',
    );

  const renderSlot = (time, isExtra) => {
    const booking = findBooking(time);

    if (booking) {
      return (
        <BookedSlot
          key={`${clinic.id}-${time}`}
          booking={booking}
          time={time}
          clinic={clinic}
          dayDiscount={dayDiscount}
          nightDiscount={isExtra ? nightDiscount : 0}
          onDragStart={() => onDragStart(booking.id, time)}
          onDragEnd={onDragEnd}
          onClick={() => !dragging && onSelectBooking?.(booking)}
        />
      );
    }

    return (
      <FreeSlot
        key={`${clinic.id}-${time}`}
        time={time}
        isExtra={isExtra}
        isDropTarget={dropTarget === time}
        onDragOver={(event) => {
          event.preventDefault();
          onDragOverSlot(time);
        }}
        onDragLeave={onDragLeaveSlot}
        onDrop={(event) => onDrop(event, time, clinic.id)}
        onClick={() => onBookSlot?.(dateKey, time, clinic)}
      />
    );
  };

  return (
    <div className="mb-6">
      <div className="mb-4 flex items-center justify-end gap-2">
        <h4 className="text-lg font-bold text-white">{clinic.name}</h4>
        <span
          className="inline-block h-3 w-3 rounded-full shadow-[0_0_8px_rgba(34,211,238,0.6)]"
          style={{ backgroundColor: clinicHex(clinic.color) }}
        />
      </div>

      <div className="grid grid-cols-2 gap-2 min-[480px]:grid-cols-3 sm:grid-cols-4 sm:gap-2.5">
        {regularSlots.map((time) => renderSlot(time, false))}
      </div>

      {extraSlots.length > 0 && (
        <>
          <div className="my-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onEditExtraHours}
              className="text-slate-500 transition-colors hover:text-white"
              aria-label="تعديل الساعات الإضافية"
            >
              <Icon name="edit-3" size={16} />
            </button>
            <div className="h-px flex-1 bg-white/10" />
            <span className="flex items-center gap-1.5 text-sm font-bold text-[#d985f5]">
              ساعات إضافية (خصم ليلي)
              <span className="text-[#f59e0b]">🌙</span>
            </span>
            <div className="h-px flex-1 bg-white/10" style={{ maxWidth: 40 }} />
          </div>

          <div className="grid grid-cols-2 gap-2 min-[480px]:grid-cols-3 sm:grid-cols-4 sm:gap-2.5">
            {extraSlots.map((time) => renderSlot(time, true))}
          </div>
        </>
      )}
    </div>
  );
}

/* ========================================================================== */

function BookedSlot({
  booking,
  time,
  clinic,
  dayDiscount,
  nightDiscount,
  onDragStart,
  onDragEnd,
  onClick,
}) {
  const statusPalette = slotBookedStyle(booking.status);
  const isAdmin = booking.bookedBy === 'admin';
  const isPending = booking.status === 'pending';
  const { t } = useI18n();
  const navigate = useNavigate();
  // المرحلة 1.3: the channel of a remote visit, and «ابدأ» once it is due
  // (the call itself arrives with 1.5; until then it opens the live session).
  const remote = booking.consultMode === 'video' || booking.consultMode === 'voice';
  const due = isDue(booking, hospitalClock());

  // A visit the admin discounted is charged something other than this
  // clinic's own price, so it is called out in white rather than left to look
  // like every other booking in the day.
  const discountedByAdmin = Number(booking.adminDiscount) > 0;
  const palette = discountedByAdmin ? SLOT_ADMIN_DISCOUNT : statusPalette;

  const isFollowup =
    booking.type === BOOKING_TYPES.FOLLOWUP || booking.bookingType === BOOKING_TYPES.FOLLOWUP;
  const typeLabel = isFollowup ? 'متابعة' : 'كشف';

  const basePrice = isFollowup
    ? Number(clinic.followup_price) || 150
    : Number(clinic.examination_price) || 300;

  // The same cumulative chain the booking itself was priced with.
  const quote = calculateBookingPrice(basePrice, {
    day: dayDiscount,
    night: nightDiscount,
    admin: booking.adminDiscount || 0,
    voucher: booking.voucherDiscount || 0,
  });
  const hasDiscount = quote.discountAmount > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onClick();
      }}
      className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl p-3 text-center transition-all hover:opacity-90"
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        // The discount glow wins: it is the louder signal, and a booking can
        // carry both marks at once.
        ...(discountedByAdmin
          ? { boxShadow: SLOT_ADMIN_DISCOUNT.glow }
          : isAdmin
            ? { boxShadow: ADMIN_GLOW }
            : {}),
      }}
    >
      <div
        className="flex items-center justify-center gap-1 text-sm font-bold"
        dir="ltr"
        style={{ color: discountedByAdmin ? SLOT_ADMIN_DISCOUNT.text : '#ffffff' }}
      >
        {statusPalette.icon && <Icon name={statusPalette.icon} size={12} className="inline" />}
        {formatTime12(time)}
        {discountedByAdmin && (
          <span className="rounded bg-slate-900 px-1 text-[9px] font-black text-amber-300">
            -{booking.adminDiscount}%
          </span>
        )}
        {booking.voucherDiscount > 0 && (
          <span
            className={`rounded border px-1.5 text-[9px] ${
              discountedByAdmin
                ? 'border-cyan-700 bg-cyan-700 text-onaccent'
                : 'border-cyan-400/50 bg-cyan-500/40 text-cyan-200'
            }`}
            style={discountedByAdmin ? undefined : { boxShadow: '0 0 8px rgba(34,211,238,0.6)' }}
          >
            -{booking.voucherDiscount}%
          </span>
        )}
      </div>

      <div
        className="flex w-full min-w-0 items-center justify-center gap-1 text-xs font-semibold"
        style={{ color: palette.text }}
      >
        {isPending && (
          <span
            className={`rounded px-1 text-[8px] ${
              discountedByAdmin ? 'bg-red-600 text-onaccent' : 'bg-red-500/40 text-red-200'
            }`}
          >
            معلّق
          </span>
        )}
        <span className="min-w-0 truncate">{isAdmin ? 'Admin' : booking.patientName}</span>
      </div>

      <div className="mt-0.5 flex flex-wrap items-center justify-center gap-1">
        <span
          className={`rounded px-1.5 text-[9px] ${
            discountedByAdmin ? 'bg-slate-900/10 text-slate-700' : 'bg-white/10 text-white/70'
          }`}
        >
          {typeLabel}
        </span>
        {remote && <ModeBadge mode={booking.consultMode} compact />}
        {hasDiscount ? (
          <>
            <span
              className={`text-[10px] line-through ${
                discountedByAdmin ? 'text-slate-500' : 'text-slate-500'
              }`}
            >
              {quote.basePrice}
            </span>{' '}
            <span
              className={`text-[10px] font-bold ${
                discountedByAdmin ? 'text-emerald-700' : 'text-emerald-400'
              }`}
            >
              {quote.finalPrice} ج.م
            </span>
          </>
        ) : (
          <span
            className={`text-[10px] ${discountedByAdmin ? 'text-slate-700' : 'text-slate-300'}`}
          >
            {quote.basePrice} ج.م
          </span>
        )}
      </div>

      {due && (
        <button
          type="button"
          className="mt-1 inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-2.5 py-1 text-[11px] font-black text-onaccent hover:bg-emerald-400"
          onClick={(event) => {
            event.stopPropagation();
            navigate('/doctor/live');
          }}
          data-testid="calendar-start"
        >
          <Icon name="play" size={11} />
          {t('queue.start')}
        </button>
      )}
    </div>
  );
}

/* ========================================================================== */

function FreeSlot({ time, isExtra, isDropTarget, onDragOver, onDragLeave, onDrop, onClick }) {
  const palette = isExtra ? SLOT_FREE_EXTRA : SLOT_FREE;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onClick();
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl p-3 text-center transition-all hover:opacity-90 ${
        isDropTarget ? 'scale-105 ring-2 ring-cyan-400' : ''
      }`}
      style={{ background: palette.bg, border: palette.border }}
    >
      <div className="text-sm font-bold" style={{ color: palette.timeColor }} dir="ltr">
        {formatTime12(time)}
      </div>
      <div
        className="flex items-center justify-center gap-1 text-xs"
        style={{ color: palette.labelColor }}
      >
        {isExtra && <Icon name="zap" size={12} />}
        متاح
      </div>
    </div>
  );
}

export default DayDetailsModal;
